/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * The Order table: every variant of the model as an independently orderable row.
 *
 * A B2B buyer orders ACROSS a model — several sizes, several tunes, different quantities of each, in
 * one pass — so the unit of purchase is a row rather than a page-level selection. That is why quantity
 * lives per row and why setting one row never clears another.
 *
 * Which attribute columns render is not decided here: `buildVariationsTable` derives them from the
 * values that differ across the set, so a category nobody configured still produces a correct table.
 * This component renders the model it is handed and holds no catalogue knowledge.
 *
 * Two layouts, one model. A table from `lg`; a card per variant below it, because a table wide enough
 * to need horizontal scrolling on a phone hides the columns that decide the purchase behind a gesture
 * the page never advertises.
 *
 * Three price columns, ONE of them real. The dealer price is what the buyer actually pays and what the
 * cart charges, so it is read from the variant's own `price` — the table and the cart cannot disagree
 * about a figure they take from the same field. MSRP/MAP and Wholesale each need their own SFCC price
 * book, neither of which exists on this instance, so they render a placeholder: a number invented for
 * a column nobody can check is how a sourcing gap stays invisible.
 *
 * Quantity-dependent dealer pricing — `tieredPrices`, needing `perPricebook: true` — is CRCB2B-371 and
 * is not attempted here.
 *
 * The footer band that totals the order and carries its two actions is `ProductOrderBar`, rendered from
 * here: this component derives the lines and owns the fetcher, because publishing the basket revision
 * and reporting the outcome are not presentation. `Save to List` is a shell pending CRCB2B-127.
 *
 * Still absent: the two identifier codes the card design shows beneath the part number.
 */
import { Fragment, type ReactElement, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher, useSearchParams } from 'react-router';
import { resourceRoutes } from '@/route-paths';
import { useToast } from '@/components/toast';
import { useBasketUpdater } from '@/providers/basket';
import type { BasketActionResponse } from '@/routes/types/action-responses';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import type { MaterialSymbolName } from '@/components/icon/glyphs';
import {
    PRICE_PLACEHOLDER,
    type OrderLine,
    type VariationColumn,
    type VariationRow,
    type VariationsFailureReason,
    type VariationsTableModel,
} from '@/lib/product/variations-table';
import { computeSpecifications, type SpecificationRow } from '@/lib/product/specifications';
import type { SpecificationAttribute } from '@/lib/product/specification-attributes';
import {
    filterParamName,
    filterVariationRows,
    reachableValues,
    readActiveFilters,
    withFilter,
    withoutFilters,
} from '@/lib/product/variation-filters';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Typography } from '@/components/typography';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipArrow, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ConfirmationDialog } from '@/components/confirmation-dialog';
import ProductOrderBar from '@/components/product-order-bar';
import QuantityPicker from '@/components/quantity-picker/quantity-picker';

import { Icon } from '@/components/icon';
import DynamicImage from '@/components/dynamic-image';
import { cn } from '@/lib/utils';
import { formatDateForLocale } from '@/lib/date-utils';
import { formatCurrency } from '@/lib/currency';

/** Rows a loading table reserves height for, so the section does not collapse and shift the page. */
const FALLBACK_ROW_COUNT = 6;

/**
 * Separator between a card's attribute values.
 *
 * A bullet, not a comma: real merchant values contain commas — `10-24t:10,12,14,16,18,21,24` is ONE
 * value — so a comma would make the boundaries between values unreadable on a line that has no column
 * headings to disambiguate them.
 *
 * The glyph alone, with no padding of its own: the frame spaces the bullet by making it a flex item on
 * an 8px gap rather than by writing spaces around it, so the row owns the spacing.
 */
const VALUE_SEPARATOR = '•';

/**
 * Above this many units the count reads as "N+" rather than as an exact figure.
 *
 * Taken from the narrow frame, which renders `50+ available` where the wide frames render exact counts
 * up to 41. Deep stock is not a number a buyer acts on — it means "as many as you want" — and an exact
 * four-digit figure would draw the eye away from the rows where the count actually constrains an order.
 */
const AVAILABILITY_EXACT_LIMIT = 50;

/** Fallback when the payload names no currency; the catalogue is single-currency per site. */
const DEFAULT_CURRENCY = 'USD';

/**
 * At or above this many units on ONE row, the quantity is confirmed before it stands.
 *
 * INCLUSIVE: 999 stands on its own, 1,000 asks. The boundary itself is the round figure an extra zero
 * most often produces, so excluding it would let through the exact value the guard exists for.
 *
 * A fat-finger guard, not a limit: an extra zero turns a 100-unit order into a 1000-unit one, and the
 * field gives no other signal that it happened. Orders at or above the threshold are legitimate — the
 * dialog accepts them, it only makes the reader look once.
 *
 * Checked on BLUR rather than on every keystroke: typing `1500` passes through `150` and `15`, and a
 * dialog that fires mid-word would interrupt the very entry it is meant to verify.
 */
const LARGE_QUANTITY_THRESHOLD = 1000;

/**
 * A variant's availability: state, how many, and from when.
 *
 * Not `InventoryMessage`, which is shared with the PDP buy box and the cart modals and renders the
 * STATE only. This table's design carries the quantity and the backorder date, and a buyer ordering
 * across a model needs the number to decide how much of each row to take. Extending the shared
 * component for one surface's extra fields would push table-specific copy into every other one.
 */
function VariantAvailability({
    inventory,
}: {
    inventory: ShopperProducts.schemas['Inventory'] | undefined;
}): ReactElement | null {
    const { t, i18n } = useTranslation('product');

    // Above the guard below, not beside its use: every hook has to run on every render, and the early
    // return for a variant with no inventory record sits between here and `formatCount`.
    const countFormat = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

    if (!inventory) return null;

    /**
     * ON HAND is what decides the state; ATS is what decides the number.
     *
     * They are not the same and cannot be swapped. Where Business Manager sets Backorder handling, ATS
     * is stock level PLUS the backorder allocation — a variant with nothing on the shelf and a 200-unit
     * backorder allocation reports `stockLevel: 0, ats: 200`. Deciding the state from ATS therefore
     * announces "In stock, 200 available" for a shipment the warehouse cannot make until the in-stock
     * date.
     */
    const onHand = inventory.stockLevel ?? 0;
    const available = inventory.ats ?? onHand;

    /**
     * Each message counts the thing IT is about.
     *
     * A variant can be both at once — 3 on the shelf and a 200-unit backorder allocation reports
     * `stockLevel: 3, ats: 203`. Counting ATS in the in-stock sentence announced "In stock. 50+
     * available" for a model that can ship exactly three, which is the most expensive way this
     * component can be wrong: the number reads as a promise about today.
     *
     * So the in-stock line counts what is on the shelf, and the backorder and pre-order lines count
     * ATS, which is what those states can actually accept.
     */
    /**
     * The figure only — the sentence around it belongs to the message keys below.
     *
     * A PLAIN string rather than a nested `t()`. The count used to be translated on its own and then
     * interpolated into an already-translated sentence, which hid the composition from translators and
     * handed i18next a string where it expected a number for `{{count}}`.
     *
     * Grouped for the locale, because a four-digit B2B quantity rendered as `1500` reads as a part
     * number rather than as fifteen hundred. `Intl.NumberFormat` also gets the separator right per
     * locale, which a template literal cannot.
     */
    const formatCount = (value: number) =>
        value > AVAILABILITY_EXACT_LIMIT
            ? `${countFormat.format(AVAILABILITY_EXACT_LIMIT)}+`
            : countFormat.format(value);
    // Month and day, no year — the frame draws `Backorder. 11 available on 07/22`. A restock date is
    // read against "how soon", and the year is noise at that range. Field ORDER is still the locale's,
    // so `en-GB` renders the same date as `22/07`.
    const restockDate = formatDateForLocale(inventory.inStockDate, i18n.language, {
        month: '2-digit',
        day: '2-digit',
    });

    if (!inventory.orderable) {
        return (
            <AvailabilityBadge icon="error" tone="text-error">
                {t('outOfStockLabel')}
            </AvailabilityBadge>
        );
    }

    if (onHand > 0) {
        return (
            <AvailabilityBadge icon="check_circle" tone="text-success-text">
                {t('inStockCount', { available: formatCount(onHand) })}
            </AvailabilityBadge>
        );
    }

    // Orderable with nothing on hand: the catalogue is promising a future shipment. The date is what
    // makes that actionable, so it leads the copy wherever the record carries one.
    if (inventory.backorderable) {
        return (
            <AvailabilityBadge icon="warning" tone="text-warning-text">
                {restockDate
                    ? t('backorderOn', { available: formatCount(available), date: restockDate })
                    : t('backorderCount', { available: formatCount(available) })}
            </AvailabilityBadge>
        );
    }

    if (inventory.preorderable) {
        return (
            <AvailabilityBadge icon="info" tone="text-info">
                {restockDate
                    ? t('preorderOn', { available: formatCount(available), date: restockDate })
                    : t('preorderCount', { available: formatCount(available) })}
            </AvailabilityBadge>
        );
    }

    return (
        <AvailabilityBadge icon="error" tone="text-error">
            {t('outOfStockLabel')}
        </AvailabilityBadge>
    );
}

/**
 * The availability badge's shell: 24px tall, 4px gap, square corners, per the frame.
 *
 * Not `ui/badge`, which is the shape the part-number cell uses: this one carries NO inline padding
 * and NO fill — the frame's availability badge is a coloured icon-plus-label sitting directly on the
 * row (`15905:52988`), where the part-number badge is a filled 8px-padded pill (`15905:52979`).
 *
 * `inline-flex` so it sits on the text baseline of its cell rather than becoming a block that pushes
 * the row taller than the 64px the other cells hold.
 */
function AvailabilityBadge({
    icon,
    tone,
    children,
}: {
    icon: MaterialSymbolName;
    tone: string;
    children: ReactNode;
}): ReactElement {
    return (
        <span
            className={cn(
                // `components/badge` per the frame: 14px at the BODY weight — `font-book`, not
                // `font-medium` — with `tracking-sram` supplying the 0.42px it specifies, since 0.03em
                // at 14px is exactly that.
                //
                // `leading-[normal]` is deliberate and not a stand-in for `leading-normal`: Tailwind's
                // named utility is 1.5, which at 14px gives a 21px line box and pushes the badge past
                // the 24px height the frame sets. CSS `normal` is the ~1.2 the 16px content box needs.
                'inline-flex h-6 min-w-6 items-center justify-center gap-1 py-1 text-center text-sm font-book leading-[normal] tracking-sram',
                tone
            )}>
            <Icon name={icon} aria-hidden className="text-[16px]" />
            {children}
        </span>
    );
}

/**
 * The variant detail a row opens to: its identity, then whatever the catalogue populated.
 *
 * ONE definition for both layouts. The card drawer and the table drawer are the same panel at two type
 * steps — 14px narrow, 16px wide — and were written twice, which is how they came to differ in nothing
 * but the thing that was supposed to differ. Written twice they also drift: a field added to one would
 * quietly be missing from the other, and nothing in the repository would notice.
 *
 * Identity leads, and the part number repeats the collapsed row on purpose: the drawer is read on its
 * own once open, and a detail panel that does not say what it describes is a puzzle.
 *
 * `description` is the catalogue's `longDescription`, NOT `name`. `name` is the short variant name shown
 * in the row above and in the part number's tooltip title; binding this row to it rendered one field
 * twice and left the description off the page entirely.
 */
function VariantDetailRows({
    product,
    detailRows,
    className,
}: {
    product: VariationRow['product'];
    /** The catalogue's attributes projected onto this ONE variant, already suppressed and ordered. */
    detailRows: readonly SpecificationRow[];
    /** The type step, which is the only thing the two layouts disagree about. */
    className?: string;
}): ReactElement {
    const { t } = useTranslation('product');

    return (
        // Label/value rows on 8px of padding above a hairline rule (`--border-color-hairline` #eaeaea →
        // `border-subtle`), label on tertiary and value right-aligned on primary. The two columns share
        // the width evenly — `flex-1` on both, not an auto label column.
        <dl className={cn('flex w-full flex-col leading-[1.5] tracking-sram', className)}>
            {product.name && <VariantDetailRow label={t('partName')} value={product.name} />}
            <VariantDetailRow label={t('partNumber')} value={product.id} />
            {product.longDescription && <VariantDetailRow label={t('description')} value={product.longDescription} />}
            {detailRows.map((detail) => (
                <VariantDetailRow key={detail.attributeId} label={detail.label} value={detail.values.join(', ')} />
            ))}
        </dl>
    );
}

/** One label/value pair inside {@link VariantDetailRows}; the identity rows and the catalogue's share it. */
function VariantDetailRow({ label, value }: { label: string; value: string }): ReactElement {
    return (
        <div className="flex w-full items-start gap-1 border-b border-border-subtle p-2">
            <dt className="flex-1 text-text-tertiary">{label}</dt>
            <dd className="flex-1 text-right text-foreground">{value}</dd>
        </div>
    );
}

/**
 * NO CEILING is applied to a row's quantity, and that is a decision rather than an omission.
 *
 * A ceiling briefly existed here, derived from ATS wherever the catalogue promised no future shipment.
 * It was removed: fulfilment splits an order that exceeds stock rather than refusing it. Three on hand
 * against a request for 500 ships three now and backorders 497, and where the backorder cannot cover
 * the remainder the line simply carries no date. That division is made by the inventory check at
 * checkout, which is the only place that can make it — availability here is a nightly snapshot, and a
 * storefront that refused the order would be refusing on figures hours out of date.
 *
 * What the row still does is STATE what it knows: `In stock. 3 available` beside a field that will take
 * 500. Informing without forbidding.
 */

interface ProductVariationsTableProps {
    /** Already derived by `buildVariationsTable`: primary column, remaining columns, one row per variant. */
    model: VariationsTableModel;
    /**
     * The catalogue's specification attributes, used to project ONE variant when its row is expanded.
     * The same list the panel unions across the whole set — which attributes belong to which surface
     * is a catalogue question, and the answer will come from PIM rather than from a second list here.
     *
     * Omitted or empty, rows do not expand — there is nothing to show, and a control that opens an
     * empty drawer is worse than no control.
     */
    specificationAttributes?: readonly SpecificationAttribute[];
    /**
     * Set when the model holds more variants than one page resolves, so the table can say so. Absent
     * is the ordinary case: the whole model is on screen.
     */
    truncation?: { total: number; used: number };
    /**
     * Set when the variant set could not be resolved into anything orderable, whatever the cause.
     *
     * Takes precedence over the rows, and has to: one of the three causes is a master that declares no
     * variants, which arrives as a perfectly well-formed set of ONE — the master's own row. Keyed on
     * the rows instead, that case would render a quantity control and an order action against a
     * product id B2C Commerce refuses at the basket.
     */
    failure?: { reason: VariationsFailureReason };
    className?: string;
}

/**
 * What the table renders instead of rows when the set could not be resolved.
 *
 * One notice for all three causes, because the reader's options are identical in each: a transport
 * failure, a master with no declared variants, and declared variants the catalogue does not return are
 * all outside anything they can act on. Which one it was goes to the server log, where the ids are and
 * where somebody can fix it.
 *
 * NO whole-product Add to Cart beside it. The band's absent content is the purchase path for a MODEL,
 * and the thing the page has instead is a master product id — which cannot be added to a basket, so an
 * action offering to would fail on every press.
 *
 * A reload rather than a retry: there is no client-side refetch to re-run. The rows come from the
 * loader, and re-running the loader is what a navigation does.
 */
function VariationsUnavailable({ className }: { className?: string }): ReactElement {
    const { t } = useTranslation('product');

    return (
        // Same box the over-narrow-filter state uses — a bordered block on the section's own padding —
        // so the two failure surfaces of this section read as one thing at two causes.
        <section className={cn('flex flex-col gap-4', className)} data-testid="product-variations-error">
            <Typography variant="h4" as="h2">
                {t('order')}
            </Typography>
            <div className="flex flex-col items-start gap-4 border border-border p-6">
                {/* `role="alert"` rather than `status`: the section the reader came to buy from is not
                    there, which is a failure to announce rather than a condition to mention. */}
                <Typography variant="p" as="p" role="alert" className="mt-0!">
                    {t('variationsUnavailable')}
                </Typography>
                <Button variant="outline" onClick={() => window.location.reload()}>
                    {t('variationsRefresh')}
                </Button>
            </div>
        </section>
    );
}

/**
 * Renders the model's variants as an order table.
 *
 * Returns `null` for an empty set rather than an empty table. A set of ONE is not empty — a standalone
 * product is a legitimate single row with no attribute columns, which is why the guard tests the rows
 * rather than the columns.
 */
export default function ProductVariationsTable({
    model,
    specificationAttributes = [],
    truncation,
    failure,
    className,
}: ProductVariationsTableProps): ReactElement | null {
    const { t, i18n } = useTranslation('product');
    // Quantity-control copy belongs to the namespace that owns the picker's other two labels, so a row's
    // field is named in the same words as its steppers rather than in this surface's own.
    const { t: tQuantity } = useTranslation('quantitySelector');
    const i18nLanguage = i18n.language;
    const [searchParams, setSearchParams] = useSearchParams();

    /**
     * The order submission.
     *
     * A fetcher rather than a `<Form>`: adding to the basket must not navigate away from the product
     * page, which is where the buyer is still working. `fetcher.Form` with a hidden field rather than an
     * imperative `submit`, so the action carries the payload the same way with or without JavaScript.
     *
     * Typed on the response the action actually answers with, so `success`, `basket` and `error` are
     * checked rather than asserted. Untyped, `data` is `any` and every read of it — including a
     * misspelled one — compiles.
     */
    const orderFetcher = useFetcher<BasketActionResponse>();
    const { addToast } = useToast();
    const updateBasket = useBasketUpdater();

    /**
     * The action's answer, surfaced.
     *
     * Without this the button was a dead control on the one path that matters most: SFCC validates
     * availability when an item is added, so asking for fifty against three on hand is REFUSED by the
     * platform — and the refusal arrived as `{ success: false }` into a component that read nothing.
     * Nothing moved, nothing was said, and the buyer had no way to tell a rejected order from a broken
     * button.
     *
     * Deduped by identity, as the other basket mutations do: React Router keeps `fetcher.data` after the
     * submission settles, so an unguarded effect re-toasts on every unrelated re-render.
     */
    const settledOrder = useRef<typeof orderFetcher.data>(null);

    /**
     * The rows a submission is carrying, so the success path clears those and only those. Read from the
     * POSTED payload rather than re-derived at settle: `orderLines` follows the current filters, and the
     * fields stay editable while a request is in flight.
     */
    const submittedIds = useRef<string[]>([]);

    useEffect(() => {
        if (orderFetcher.state !== 'idle' || !orderFetcher.data) return;
        if (settledOrder.current === orderFetcher.data) return;

        settledOrder.current = orderFetcher.data;

        if (orderFetcher.data.success) {
            // Publish the revision so the header count and the cart drawer follow, matching every other
            // basket mutation handler.
            if (orderFetcher.data.basket) updateBasket(orderFetcher.data.basket);

            /*
             * A toast and the basket publish, and DELIBERATELY NOT the mini cart.
             *
             * Every other add path in this storefront opens the cart drawer on success, so the omission
             * here is an exception rather than an oversight — and the reason is the interaction this
             * surface supports. A single add ENDS a shopper's pass: they chose one thing and the drawer
             * confirms it. Ordering across a model does not: a B2B buyer works down a table of sixteen
             * rows, and the drawer would cover the rows they are still reading, on a page whose whole
             * purpose is comparing them. The frames for this band show no drawer either.
             *
             * The confirmation the buyer needs is still given twice — the toast says it happened, and
             * the published revision moves the header count — without taking the page away from them.
             *
             * If this is ever reconsidered, reconsider it as a decision about the ORDER TABLE, not as a
             * consistency fix: making it match the other paths is precisely what was rejected.
             */
            addToast(t('orderAdded'), 'success');

            /*
             * Disarm the table, because nothing else will: `cartSetAdd` is deliberately absent from the
             * PDP's revalidation list (`lib/revalidation/routes/product.ts`), so a successful order
             * re-runs no loader and remounts nothing. Left alone, the band stays armed with the order it
             * just placed and a second click adds all of it again — and in B2B a single row can carry
             * 1,500 units.
             *
             * The SUBMITTED rows only: a quantity on a filtered-out row was never ordered — the notice
             * above the band says so — so wiping it would discard work the buyer expects to find when
             * they clear the filter. `acceptedQuantities` is pruned to match.
             */
            setQuantities((current) => {
                const next = { ...current };

                submittedIds.current.forEach((id) => delete next[id]);

                return next;
            });
            submittedIds.current.forEach((id) => delete acceptedQuantities.current[id]);

            return;
        }

        // The action's own message, not a generic one: SFCC says WHY it refused — most usefully that a
        // quantity exceeded what is available — and replacing that with "something went wrong" would
        // throw away the only sentence that tells the buyer what to change. `error` is optional on the
        // response, so the fallback covers a failure that names no reason.
        addToast(t('orderAddFailed', { error: orderFetcher.data.error?.message ?? '' }), 'error');
    }, [orderFetcher.state, orderFetcher.data, addToast, updateBasket, t]);

    // `formData` holds one submission's payload and keeps its identity for the life of it, so this
    // records each order once, at the point of no return.
    useEffect(() => {
        const posted = orderFetcher.formData?.get('productItems');

        if (typeof posted !== 'string') return;

        submittedIds.current = (JSON.parse(posted) as OrderLine[]).map((line) => line.productId);
    }, [orderFetcher.formData]);

    /**
     * Quantity per row, keyed by variant id.
     *
     * A record rather than one control's value: the whole point of the table is accumulating an order
     * across several rows, so entering a quantity on one row must leave the others untouched.
     *
     * Local state rather than the URL, unlike the filters: a half-entered order is not something to
     * restore on refresh or hand to someone else in a link.
     */
    const [quantities, setQuantities] = useState<Record<string, string>>({});

    /**
     * The last quantity that STOOD on each row — below the threshold, or confirmed at or above it.
     *
     * A ref rather than state, because nothing renders from it. It exists to dedupe the confirmation: a
     * figure already confirmed must not be questioned again every time the buyer tabs back through the
     * row it sits on.
     */
    const acceptedQuantities = useRef<Record<string, string>>({});

    /** The row awaiting confirmation, if any. One at a time — the dialog is modal. */
    const [pendingQuantity, setPendingQuantity] = useState<{ id: string; quantity: number } | null>(null);

    /**
     * Settles a row's quantity when its field is left: normalises it, then asks about it if it is
     * implausibly large.
     *
     * Normalising is not tidying: handing `QuantityPicker` an `onBlur` opts OUT of the hook's own
     * auto-correction, so an empty or malformed field would otherwise stay that way.
     *
     * BLUR ONLY, deliberately. The +/− buttons do not settle: a step changes the figure visibly, one unit
     * at a time, under the buyer's own hand — so a quantity reached that way is a deliberate act rather
     * than the mistyped one the confirmation exists to catch. A value stepped past the threshold is
     * therefore accepted unquestioned until the field is next left.
     */
    const settleQuantity = (id: string) => {
        const parsed = Number.parseInt(quantities[id] ?? '0', 10);
        // Normalised, never capped. Supplying `onBlur` opts out of the hook's own handling, so an empty
        // or malformed field has to be zeroed here — but a figure above what is available stands, and
        // is split at checkout rather than refused on a nightly snapshot.
        const settled = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
        const settledValue = String(settled);

        setQuantities((current) => ({ ...current, [id]: settledValue }));

        // Asked once per value, not once per blur. Without the comparison, a confirmed 1,000 would
        // re-open the dialog every time the buyer tabbed back through the row it sits on.
        //
        // Inclusive: the threshold itself is confirmed, so 999 stands and 1,000 asks.
        if (settled >= LARGE_QUANTITY_THRESHOLD && acceptedQuantities.current[id] !== settledValue) {
            setPendingQuantity({ id, quantity: settled });

            return;
        }

        acceptedQuantities.current[id] = settledValue;
    };

    /**
     * Set by an explicit Yes, so the close that Radix performs immediately afterwards is not read as a
     * decline.
     *
     * `AlertDialogAction` closes the dialog itself, so `onOpenChange(false)` fires on the confirm path as
     * well as the cancel and dismiss ones — every answer ends in a close. Previously that was invisible,
     * because declining restored the accepted value and confirming had just set it to the figure being
     * confirmed, so both paths landed on the same number by coincidence. Clearing the row on decline
     * removes the coincidence and the double-fire would wipe a quantity the buyer had just approved.
     */
    const confirmedQuantity = useRef(false);

    const confirmPendingQuantity = () => {
        if (pendingQuantity) {
            confirmedQuantity.current = true;
            acceptedQuantities.current[pendingQuantity.id] = String(pendingQuantity.quantity);
        }

        setPendingQuantity(null);
    };

    /**
     * Declining CLEARS the row to zero rather than restoring what it held before.
     *
     * The buyer did not confirm the figure, so the row has no quantity anyone stands behind — and zero is
     * the state that excludes it from the order until it is filled in again deliberately. Restoring the
     * previous value would leave the row carrying a number the buyer never re-affirmed, which reads as
     * "your edit was ignored" rather than as "start that entry again".
     *
     * The accepted ref follows the field, so it keeps mirroring the last value that actually stood.
     */
    const rejectPendingQuantity = () => {
        // An explicit Yes has already answered; the close that follows it is not a decline. See
        // {@link confirmedQuantity}.
        if (confirmedQuantity.current) {
            confirmedQuantity.current = false;

            return;
        }

        if (pendingQuantity) {
            acceptedQuantities.current[pendingQuantity.id] = '0';

            setQuantities((current) => ({ ...current, [pendingQuantity.id]: '0' }));
        }

        setPendingQuantity(null);
    };

    /**
     * Which rows are open, by variant id.
     *
     * A set rather than one open id: expansion is local to its row, so opening a second must not close
     * the first — a buyer comparing two variants would otherwise lose the one they were reading.
     */
    const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
    const toggleExpanded = (id: string) =>
        setExpanded((current) => {
            const next = new Set(current);

            if (!next.delete(id)) next.add(id);

            return next;
        });

    /**
     * Whether rows offer a detail drawer at all.
     *
     * Two conditions, and the second is not defensive. A set of ONE projects `computeSpecifications`
     * over a single member — which is byte-for-byte what the specifications panel above already renders
     * for that same product, from the same function and the same attribute list. The drawer cannot
     * differ from the panel, so on a standalone the chevron opens a verbatim copy of the section above
     * it and the expander column costs a column for nothing.
     *
     * Measured on the whole SET, not on the visible rows: a filter narrowing a real model to one row
     * must not take its drawers away, because those rows still differ from the ones the filter hid.
     */
    const canExpand = specificationAttributes.length > 0 && model.rows.length > 1;

    /**
     * Columns the table has beyond the derived attribute ones: part number, thumbnail, availability,
     * quantity, and the expander where it renders.
     *
     * Named because the expanded row spans the whole table, and a `colSpan` counted by hand drifts the
     * moment a column is added — leaving the drawer either short of the last column or overflowing it.
     */
    const fixedColumnCount = 8 + (canExpand ? 1 : 0);

    /**
     * The dealer price, formatted for the active locale.
     *
     * `price` is the figure the cart charges, so reading it here keeps the table and the cart from
     * disagreeing — a row that quotes one number and adds another is worse than a row with no number.
     * A product with no price renders the placeholder rather than a zero.
     */
    const formatPrice = (product: VariationRow['product']) =>
        typeof product.price === 'number' && Number.isFinite(product.price)
            ? formatCurrency(product.price, i18nLanguage, product.currency ?? DEFAULT_CURRENCY)
            : PRICE_PLACEHOLDER;

    /** The variant's own thumbnail; `small` where the catalogue has one, otherwise the gallery image. */
    const thumbnailFor = (product: VariationRow['product']) =>
        (
            product.imageGroups?.find((group) => group.viewType === 'small') ??
            product.imageGroups?.find((group) => group.viewType === 'large')
        )?.images?.[0];

    // Checked BEFORE the rows, because the state that most needs it has one: a master declaring no
    // variants resolves to its own row, and that row is unbuyable rather than merely uninteresting.
    if (failure) return <VariationsUnavailable className={className} />;

    if (!model.rows.length) return null;

    const columns = model.primary ? [model.primary, ...model.secondary] : model.secondary;

    /**
     * The primary dimension's column is right-aligned; every other attribute column is left.
     *
     * Read off the frame, where `Eye to Eye` — the primary dimension — is the one attribute column
     * whose header AND cells set `text-right` (`15905:52966` / `15905:52981`), while `Shaft eyelet`
     * and `Body Eyelet` beside it are left (`15905:52967` / `15905:52982`). Keyed to the primary
     * dimension rather than to "looks numeric", because the model already names that column and a
     * value-sniffing rule would flip a column's alignment when the catalogue changes one row.
     */
    const alignFor = (column: VariationColumn) =>
        column.attributeId === model.primary?.attributeId ? ('right' as const) : ('left' as const);

    // Filters read from the URL, and the derivation lives in one module the specifications panel reads
    // too — written twice, the two would drift and the page would state a value the filter excluded.
    const activeFilters = readActiveFilters(model, searchParams);
    const visibleRows = filterVariationRows(model, activeFilters);

    /**
     * A row's entered quantity as a number, or 0.
     *
     * `parseInt` rather than a cast: the state is the field's string, which is briefly empty while being
     * retyped. Shared by the submission and by the filtered-out notice, so the two cannot disagree about
     * which rows are carrying something.
     */
    const quantityFor = (productId: string) => {
        const quantity = Number.parseInt(quantities[productId] ?? '0', 10);

        return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
    };

    /**
     * The lines the order action will submit: the VISIBLE rows carrying a quantity above zero.
     *
     * A row at zero is one the buyer took off the order, and a hidden row is one they cannot see to
     * reconsider — submitting either would add something nobody asked for.
     */
    const orderLines = visibleRows.flatMap((row) => {
        // Belt and braces against the un-orderable row: its picker is disabled, so it cannot normally
        // hold a figure, but a quantity entered BEFORE the catalogue went out of stock survives in
        // state — and one such line is enough to make SFCC reject the whole batched add.
        if (row.product.inventory?.orderable === false) return [];

        const quantity = quantityFor(row.product.id);

        return quantity > 0 ? [{ productId: row.product.id, quantity }] : [];
    });

    // Distinct SKUs carrying a quantity, not units. Settled with Tyler: the action names how many of the
    // model's ITEMS are being ordered, which is a count of rows rather than a sum of their quantities.
    // The running total beside it is still money over units, so the two figures answer different
    // questions on purpose.
    const orderItemCount = orderLines.length;

    /**
     * Rows carrying a quantity that the filters have taken off screen.
     *
     * Excluding them from the order is the documented decision; doing it SILENTLY is the hazard — the
     * quantities stay in state, so clearing the filter later re-arms the button with figures that were
     * never ordered.
     *
     * Counted in ROWS, like `orderItemCount` beside it, so the notice and the button speak in one unit.
     * Un-orderable rows are left out: they would not join the order even unfiltered, so offering to
     * "include them" would be an offer the order cannot keep.
     */
    const visibleIds = new Set(visibleRows.map((row) => row.product.id));
    const hiddenItemCount = model.rows.filter(
        (row) =>
            !visibleIds.has(row.product.id) &&
            row.product.inventory?.orderable !== false &&
            quantityFor(row.product.id) > 0
    ).length;

    /**
     * The running total, or the placeholder where it cannot be stated honestly.
     *
     * Every selected row must carry a price for a total to mean anything: summing the priced ones and
     * presenting the result as THE total would understate what the buyer is about to be charged, which
     * is worse than declining to state it. The label beside the figure names the price basis it summed,
     * because a row carries more than one price point and a bare figure cannot say which was used.
     *
     * An EMPTY selection is a stated zero, not a placeholder. Nothing selected costs nothing, which the
     * page can say exactly — and saying it keeps the figure a currency amount from the first render, so
     * entering the first quantity changes the number rather than changing what kind of thing is there.
     * The placeholder is reserved for the one case the page genuinely cannot answer: rows ARE selected
     * and at least one of them has no price to sum.
     */
    const orderTotal = (() => {
        const currency = model.rows[0]?.product.currency ?? DEFAULT_CURRENCY;

        if (!orderLines.length) return formatCurrency(0, i18nLanguage, currency);

        let total = 0;

        for (const line of orderLines) {
            const price = model.rows.find((row) => row.product.id === line.productId)?.product.price;

            if (typeof price !== 'number' || !Number.isFinite(price)) return undefined;

            total += price * line.quantity;
        }

        return formatCurrency(total, i18nLanguage, currency);
    })();

    // `replace` so a dropdown does not push a history entry per change, and `preventScrollReset` so
    // narrowing a table does not throw the reader back to the top of the page.
    const navigateOptions = { replace: true, preventScrollReset: true } as const;
    const setFilter = (attributeId: string, value: string) =>
        setSearchParams(withFilter(searchParams, attributeId, value), navigateOptions);
    const clearFilters = () => setSearchParams(withoutFilters(searchParams, model), navigateOptions);

    return (
        // `gap-4` — `var(--spacing-4)`, the 16px the frames put between the header block and the rows,
        // at both widths. Flex gap skips a child that is `display: none`, so the hidden layout costs no
        // gap and the two never both count.
        //
        // No margin of its own: the panel above spaces its children with a gap, and a component that
        // also carried an offset would add to it. Spacing between sections belongs to the layout.
        <section className={cn('flex flex-col gap-4', className)} data-testid="product-variations-table">
            {/* Header. A column at 16px below `lg` — heading block, then filters, per the narrow
                frame (`14092:37147`) — and a row at 40px from `lg`, per `var(--spacing-10)` on the
                desktop frame. `items-start` at BOTH widths: it is the narrow frame's own
                `align-items: flex-start`, and from `lg` it is what aligns the filters to the top of
                the heading rather than to its baseline. */}
            <div className="flex flex-col items-start gap-4 lg:flex-row lg:gap-10">
                {/* `heading/sm` — the `h4` variant, which is the 32px step. Its `tracking-sram` resolves
                    to the 0.96px the frame specifies, since 0.03em at 32px is exactly that. The element
                    is an `h2`: the variant names the type step, not the document outline. */}
                <div className="flex flex-col">
                    <Typography variant="h4" as="h2">
                        {t('order')}
                    </Typography>
                    {/* `body/default` at the secondary colour, per the frame — the 16px step, not the
                        14px `muted` variant, whose `tracking-sram` would also resolve to 0.42px rather
                        than the 0.48px specified.

                        `mt-0!` is required, not tidying: the `p` variant carries
                        `[&:not(:first-child)]:mt-6`, sensible for prose but wrong for a count sitting
                        directly under its heading. `:not()` raises the selector's specificity above a
                        plain `mt-0`, so the important suffix is what actually wins. */}
                    <Typography
                        variant="p"
                        as="p"
                        className="mt-0! text-muted-foreground"
                        data-testid="product-variations-count">
                        {t('variationCount', { count: visibleRows.length })}
                    </Typography>
                </div>

                {/* One filter per DECLARED variation attribute, uncapped — including the one heading
                    the columns, and including any the resolved set does not vary in. The filter set
                    follows what the master says the model's dimensions are, not what these rows happen
                    to show, so it is `model.filters` rather than the columns.

                    The reference frame shows two because the product it was drawn from declares that
                    many beyond the primary. Capping at the frame's count would hide a dimension a buyer
                    filters on, leaving them to read sixteen rows by eye.

                    `flex-wrap` is what makes an uncapped count safe: each group is a fixed 180px with a
                    16px gap, so five fit on the desktop frame and any number wraps below it. */}
                {model.filters.length > 0 && (
                    <div className="flex flex-wrap items-center gap-4" data-testid="product-variations-filters">
                        {model.filters.map((column) => {
                            const name = filterParamName(column.attributeId);

                            return (
                                // 180px wide, label above the control with an 8px gap, per the frame.
                                //
                                // The wrapper override is load-bearing: `NativeSelect` puts its
                                // `<select>` inside a `w-fit` div so the control sizes to its content,
                                // and it exposes a className for the `<select>` only. A `w-full` on the
                                // select therefore resolves against a shrink-wrapped parent and leaves
                                // the field narrower than its 180px group — which reads as uneven gaps
                                // between filters rather than as a too-narrow control.
                                <div
                                    key={column.attributeId}
                                    className="flex w-45 flex-col items-start gap-2 [&_[data-slot=native-select-wrapper]]:w-full">
                                    {/* `components/label-sm` per the frame's Select Input
                                        (`15905:52891`): 14px on a 20px line box at 0.1em — the 1.4px
                                        the node reports, since `letterSpacing: 10` in this file is
                                        percent — on `--text-color-primary`, capitalised.
                                        Deliberately NOT `FormLabel`, which is the 18px Text Input
                                        label; this frame's filter uses the smaller step. */}
                                    <label
                                        htmlFor={name}
                                        className="text-sm font-book leading-5 tracking-[0.1em] text-foreground capitalize">
                                        {column.label}
                                    </label>
                                    <NativeSelect
                                        id={name}
                                        // `md` is the 40px control the frame specifies —
                                        // `var(--size-10)` — against the primitive's 48px `lg` default.
                                        // The type stays 16px at both sizes; `md` reaches 40 by
                                        // reducing padding alone, so the value reads identically.
                                        size="md"
                                        className="w-full"
                                        value={searchParams.get(name) ?? ''}
                                        onChange={(event) => setFilter(column.attributeId, event.target.value)}>
                                        <NativeSelectOption value="">{t('filterAny')}</NativeSelectOption>
                                        {reachableValues(model, activeFilters, column).map((value) => (
                                            <NativeSelectOption key={value} value={value}>
                                                {value}
                                            </NativeSelectOption>
                                        ))}
                                    </NativeSelect>
                                </div>
                            );
                        })}

                        {/* Clearing every filter at once, available whenever anything is set — not
                            only from the empty result below. With five filters on a production model,
                            returning to the full list otherwise costs five separate selections. */}
                        {activeFilters.length > 0 && (
                            <Button
                                // Matched to the PLP's own `Clear all`
                                // (`category-refinements/active-filters`), which is what actually
                                // ships beside that page's filter chips: an underlined text link on
                                // the secondary tier, not a bordered control.
                                //
                                // Figma's Code Connect snippet for the same node offers
                                // `variant="ghost" size="sm"`, and it is NOT followed here. Ghost
                                // renders a padded box that reads as a button, so the two surfaces
                                // showed the same affordance two different ways — the running PLP is
                                // the stronger source for a control that already exists in code.
                                //
                                // The classes are the PLP's, deliberately duplicated rather than
                                // extracted: two call sites do not yet justify a shared component,
                                // and a third should prompt one rather than a third copy.
                                //
                                // `h-10` with `self-end` aligns it to the select, not to the filter
                                // group — whose height includes the labels above each control.
                                variant="link"
                                className="m-0 h-10 cursor-pointer self-end p-0 text-sm text-muted-foreground underline hover:text-foreground"
                                onClick={clearFilters}
                                data-testid="product-variations-clear-filters">
                                {t('clearFilters')}
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* A partial model says so. Every row on screen is correct, which is exactly why silence
                here is dangerous: nothing about the table looks wrong when rows are missing from it.
                `role="status"` rather than `alert` — it is a condition of the page, not an error the
                reader caused. */}
            {truncation && (
                <p role="status" className="text-sm text-muted-foreground" data-testid="product-variations-truncated">
                    {t('variationsTruncated', { used: truncation.used, total: truncation.total })}
                </p>
            )}

            {/* A combination matching nothing is explained and reversible. An empty table body would
                read as a failed load rather than as an over-narrow filter. */}
            {visibleRows.length === 0 ? (
                <div className="flex flex-col items-start gap-4 border border-border p-6">
                    <Typography variant="p" as="p" className="mt-0!">
                        {t('noMatchingVariations')}
                    </Typography>
                    {/* `outline` at the default step, NOT the chip row's ghost `sm`. Same copy and
                        same action, deliberately weightier: there it ends a row of working controls
                        and competes with none of them, while here it is the ONLY thing on an empty
                        panel and the single way back to a populated table. A ghost control in that
                        position reads as a caption rather than as the way out. No frame covers this
                        state — the PLP node draws no empty result — so it is a judgement rather than
                        a reading. */}
                    <Button variant="outline" onClick={clearFilters}>
                        {t('clearFilters')}
                    </Button>
                </div>
            ) : (
                <>
                    {/* NARROW — a card per variant. Both layouts are in the DOM and one is hidden by
                        CSS, rather than switching on a measured viewport: a media query read in
                        JavaScript is unavailable during SSR, so the server would have to guess and the
                        client would correct it — a hydration mismatch on the page's purchase path.
                        `display: none` also takes the hidden layout out of the tab order and off the
                        accessibility tree, so the duplicate never reaches assistive tech. */}
                    <ul
                        data-testid="product-variations-cards"
                        className="divide-y divide-border border border-border lg:hidden">
                        {visibleRows.map((row) => {
                            const id = row.product.id;
                            const isOpen = expanded.has(id);
                            const detailRows = isOpen
                                ? computeSpecifications([row.product], specificationAttributes)
                                : [];
                            const detailImage = isOpen
                                ? row.product.imageGroups?.find((group) => group.viewType === 'large')?.images?.[0]
                                : undefined;
                            const values = columns
                                .map((column) => row.values[column.attributeId])
                                .filter((value): value is string => Boolean(value));

                            return (
                                // The box belongs to the LIST — `Frame 2609439` (`13869:82380`) carries
                                // `1px solid var(--border-color-default)` — and the rules between cards
                                // come from `divide-y`, not from a `border-b` on each card.
                                //
                                // The row component does declare its own bottom border, and reproducing
                                // it literally is wrong here: in Figma the last row's rule and the
                                // container's bottom stroke land on the same line and read as one, while
                                // in CSS they are two adjacent 1px borders and the last card would carry
                                // a visibly double rule. `divide-y` draws the same 15 lines the frame
                                // shows and leaves the 16th to the box.
                                <li key={id} className="flex flex-col items-start p-3">
                                    <div className="flex w-full items-start gap-2">
                                        {/* The frame makes the WHOLE card the expand control — a
                                            `<button>` wrapping everything, chevron included. Rendered
                                            here as the chevron alone, because the card also holds the
                                            quantity picker and its two controls: a button inside a
                                            button is invalid HTML, and axe's `nested-interactive`
                                            fails it. The hit area is the 48px box the frame draws
                                            rather than the whole row. */}
                                        {canExpand && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon-lg"
                                                className="shrink-0"
                                                aria-expanded={isOpen}
                                                aria-controls={`${id}-card-details`}
                                                onClick={() => toggleExpanded(id)}>
                                                <span className="sr-only">{t('rowDetails')}</span>
                                                <Icon
                                                    name={isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                                                    aria-hidden
                                                />
                                            </Button>
                                        )}
                                        <div className="flex min-w-0 flex-1 flex-col gap-2">
                                            <div className="flex w-full items-center justify-between gap-2">
                                                {/* `body/default` at 80% black, per the frame — a
                                                    softened primary rather than the secondary tier the
                                                    lines beneath it use. `text-foreground/80` IS that
                                                    value: `--foreground` is black. */}
                                                <span className="min-w-0 flex-1 text-base leading-[1.5] tracking-sram text-foreground/80">
                                                    {id}
                                                </span>
                                                <QuantityPicker
                                                    size="sm"
                                                    // Zero is the resting state of every row, so the
                                                    // control has to be able to return to it. Without
                                                    // the explicit floor the picker holds at 1 and a
                                                    // row taken off the order cannot be put back.
                                                    min={0}
                                                    // NOT the "inform without forbidding" case, which is
                                                    // about asking for more than is on the shelf of an
                                                    // ORDERABLE item and which fulfilment splits. SFCC
                                                    // refuses an un-orderable row outright, and the
                                                    // order posts as one `addItemToBasket` call, so that
                                                    // row takes every other one down with it.
                                                    //
                                                    // `=== false` rather than falsy: a variant with no
                                                    // inventory record says nothing about availability
                                                    // (`VariantAvailability` renders null for it), and
                                                    // unknown is not out of stock.
                                                    disabled={row.product.inventory?.orderable === false}
                                                    value={quantities[id] ?? '0'}
                                                    // Named for the PART NUMBER, which is how this
                                                    // surface identifies a row everywhere else — the
                                                    // badge column, the tooltip trigger, the
                                                    // over-threshold dialog — and which, unlike the
                                                    // variant name, is unique by definition. Without it
                                                    // sixteen rows announce sixteen fields all called
                                                    // "Quantity".
                                                    aria-label={tQuantity('quantityForProduct', {
                                                        productName: id,
                                                    })}
                                                    productName={row.product.name ?? id}
                                                    onChange={(stringValue) => {
                                                        setQuantities((current) => ({
                                                            ...current,
                                                            [id]: stringValue,
                                                        }));
                                                    }}
                                                    onBlur={() => settleQuantity(id)}
                                                />
                                            </div>
                                            {/* UPC, in one of the two identifier slots the frame draws
                                                under the part number.

                                                Those slots hold `CS-XG-1290-E1` and a second dotted
                                                number in the frame — placeholder content, and provably
                                                so: `CS` is SRAM's cassette prefix sitting on a rear
                                                shock row, and both lines repeat identically on every
                                                card while the part numbers above them differ. Neither
                                                field is identified anywhere, so rather than guess at
                                                them the slot takes the identifier the row genuinely
                                                has and the wide layout already carries.

                                                Same 14px secondary treatment as the lines beneath it —
                                                unlabelled, like every other value on a card, because a
                                                card has no column headings to label anything. */}
                                            {row.product.upc && (
                                                <span className="text-sm leading-[1.5] tracking-sram text-muted-foreground">
                                                    {row.product.upc}
                                                </span>
                                            )}
                                            {/* One line for every distinguishing attribute. The card has
                                                no column headings to label them, so they read
                                                positionally — which is why the separator has to be
                                                visible punctuation.

                                                A flex row rather than a joined string: the frame sets
                                                each value and each bullet as its own item on an 8px gap
                                                (`I13869:82381;13935:38454`), which a `' • '` inside one
                                                text node cannot reproduce. `flex-wrap` because the
                                                narrow frame was drawn with three values and a model
                                                that differs in five would otherwise overflow. */}
                                            {values.length > 0 && (
                                                <p className="flex w-full flex-wrap items-start gap-2 px-1 text-sm leading-[1.5] tracking-sram text-muted-foreground">
                                                    {values.map((value, index) => (
                                                        <Fragment key={value}>
                                                            {index > 0 && <span aria-hidden>{VALUE_SEPARATOR}</span>}
                                                            <span>{value}</span>
                                                        </Fragment>
                                                    ))}
                                                </p>
                                            )}
                                            {/* Price and availability stack with no gap between them,
                                                per the frame: two 24px lines reading as one block. */}
                                            <div className="flex flex-col items-start">
                                                <span className="text-base leading-[1.5] tracking-sram text-foreground">
                                                    {formatPrice(row.product)}
                                                </span>
                                                <VariantAvailability inventory={row.product.inventory} />
                                            </div>
                                        </div>
                                    </div>
                                    {/* `Open=true, Size=Mobile` (`13935:40862`). Sibling of the row block
                                        with NO gap between them and inside the card's own 12px padding,
                                        exactly as the variant stacks them.

                                        The rows are the wide drawer's, one type step down — 14px here
                                        against 16px there — and the image is 174px against 423px. Same
                                        component, two sizes; neither is derived from the other. */}
                                    {isOpen && (
                                        <div
                                            id={`${id}-card-details`}
                                            className="flex w-full flex-col items-start bg-secondary p-3">
                                            {detailImage?.link && (
                                                // Leading the panel at its drawn size rather than
                                                // stretching to the card: the frame sets a 174px square
                                                // on white and leaves the rest of the line empty.
                                                //
                                                // `bg-background`, not `bg-white`: the frame's fill is
                                                // `surface-page`, which is what `--background` maps to.
                                                // A product cut out on white needs the plate behind it
                                                // to be the page's own surface, and naming the token
                                                // rather than the colour keeps it that way under a
                                                // theme.
                                                <DynamicImage
                                                    src={detailImage.link}
                                                    alt={detailImage.title ?? row.product.name ?? id}
                                                    widths={[174, 348]}
                                                    className="size-[174px] shrink-0 bg-background"
                                                    // `className` lands on DynamicImage's wrapper `div`, never on
                                                    // the `img` — without this the `img` keeps its intrinsic
                                                    // dimensions and overflows the square.
                                                    imageProps={{ className: 'size-full object-contain' }}
                                                />
                                            )}
                                            {/* The wide drawer's rows one type step down — 14px here
                                                against 16px there, which is the only difference between
                                                the two. The identity rows matter MORE at this width: the
                                                tooltip that carries the variant's name on desktop is a
                                                hover affordance and does not exist on touch, so without
                                                them a phone never sees what the item is called. */}
                                            <VariantDetailRows
                                                product={row.product}
                                                detailRows={detailRows}
                                                className="text-sm"
                                            />
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    {/* WIDE — the table. Hidden rather than unmounted, for the SSR reason above. */}
                    <Table
                        containerClassName="hidden lg:block"
                        // The table is focusable as a scroll region, so it has to say what it is.
                        containerProps={{ 'aria-label': t('order') }}>
                        <TableHeader>
                            <TableRow>
                                {/* The expander's column carries no heading text — its cells hold a
                                    control, not data. `sr-only` names it anyway, so the column is not
                                    an unlabelled cell in a screen reader's table navigation. */}
                                {canExpand && (
                                    <TableHead scope="col">
                                        <span className="sr-only">{t('rowDetails')}</span>
                                    </TableHead>
                                )}
                                <TableHead scope="col">
                                    <span className="sr-only">{t('imageAlt')}</span>
                                </TableHead>
                                <TableHead scope="col">{t('partNumber')}</TableHead>
                                <TableHead scope="col">{t('upc')}</TableHead>
                                {columns.map((column) => (
                                    <TableHead key={column.attributeId} scope="col" align={alignFor(column)}>
                                        {column.label}
                                    </TableHead>
                                ))}
                                {/* TODO(CRCB2B-371): MSRP/MAP has its own SFCC price book (MSRP-USD)
                                    and arrives as the list price. Not configured on this instance, so
                                    the column renders a placeholder rather than a number nobody can
                                    check. */}
                                {/* All three price columns are right-aligned, per the frame
                                    (`15905:52969`, `15905:52970`, `16150:94924`) — amounts align on
                                    their last digit so magnitudes compare down the column. */}
                                <TableHead scope="col" align="right">
                                    {t('msrpMap')}
                                </TableHead>
                                {/* TODO(CRCB2B-371): Wholesale is a separate price book and renders
                                    STRUCK THROUGH once sourced — it is a reference figure, not what the
                                    dealer pays. Placeholder until that book exists. */}
                                <TableHead scope="col" align="right">
                                    {t('wholesale')}
                                </TableHead>
                                {/* The operative figure: what the dealer actually pays, and what the
                                    cart charges. Sourced from the variant's own price, so the table and
                                    the cart cannot disagree. Quantity-dependent tiers are CRCB2B-371. */}
                                <TableHead scope="col" align="right">
                                    {t('dealerPrice')}
                                </TableHead>
                                <TableHead scope="col">{t('availability')}</TableHead>
                                {/* The heading is right-aligned where its cells are not: the frame
                                    sets `text-right` on the header (`15905:52972`) and leaves the
                                    control at the cell's leading edge (`15905:52991`), so the label
                                    sits over the column's outer edge rather than over the picker. */}
                                <TableHead scope="col" align="right">
                                    {t('quantity')}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {visibleRows.map((row) => {
                                const id = row.product.id;
                                const isOpen = expanded.has(id);
                                const detailRows = isOpen
                                    ? computeSpecifications([row.product], specificationAttributes)
                                    : [];
                                const thumbnail = thumbnailFor(row.product);
                                const detailImage = isOpen
                                    ? row.product.imageGroups?.find((group) => group.viewType === 'large')?.images?.[0]
                                    : undefined;

                                return (
                                    <Fragment key={id}>
                                        <TableRow>
                                            {canExpand && (
                                                // The frame's `display: flex` describes the cell's auto
                                                // layout, NOT a `display` for the `<td>`. Setting it on the
                                                // cell itself drops it out of the row's table formatting
                                                // context and the browser wraps it in an anonymous cell, so
                                                // the control and the thumbnail stack instead of sitting in
                                                // their own columns. The flex box goes inside.
                                                <TableCell>
                                                    <div className="flex items-center justify-end gap-2">
                                                        {/* Icon Button, Ghost/Neutral at the LARGE step —
                                                        a 48px box holding a 24px glyph, per the
                                                        frame's `Icon Button` (`15905:52974`). The
                                                        shared Button carries the focus ring and the
                                                        hover fill, so neither is hand-rolled here.

                                                        The glyph is `keyboard_arrow_down`, not the
                                                        frame's `expand_more`: the two are the same
                                                        chevron in Material Symbols, and the icon font
                                                        is a build-time subset, so naming the second
                                                        alias would mean regenerating the WOFF2 for a
                                                        glyph already in it. */}
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon-lg"
                                                            aria-expanded={isOpen}
                                                            aria-controls={`${id}-details`}
                                                            onClick={() => toggleExpanded(id)}>
                                                            <span className="sr-only">{t('rowDetails')}</span>
                                                            <Icon
                                                                name={
                                                                    isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
                                                                }
                                                                aria-hidden
                                                            />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <div className="flex items-center justify-end gap-2">
                                                    {thumbnail?.link && (
                                                        // 48px square inside a 1px `--border` box, per the
                                                        // frame (`15905:52978`). The rule is on the image
                                                        // rather than the cell: it frames the thumbnail,
                                                        // and a variant with no image draws nothing.
                                                        <DynamicImage
                                                            src={thumbnail.link}
                                                            alt={thumbnail.title ?? row.product.name ?? id}
                                                            widths={[96]}
                                                            className="size-12 border border-border"
                                                            imageProps={{ className: 'size-full object-contain' }}
                                                        />
                                                    )}
                                                </div>
                                            </TableCell>
                                            {/* The part number is a filled badge, not bold text: the frame
                                            puts it in `Badge` at the neutral-subtle tier — #f5f5f5
                                            fill, #4a4a4a label (`15905:52979`) — which is
                                            `neutral`/`subtle` here.

                                            The long description hangs off it as a tooltip. The trigger
                                            is a real `<button>` (Badge's `asChild` over Radix's
                                            trigger) rather than the span alone, because a hover-only
                                            affordance on an unfocusable element is unreachable by
                                            keyboard and by touch. It carries no action — which is what
                                            the Tooltip primitive requires of a trigger.

                                            Rendered only where the catalogue HAS a description: an
                                            empty tooltip is a control that punishes the reader for
                                            using it. */}
                                            <TableCell>
                                                {row.product.longDescription ? (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Badge asChild variant="neutral" appearance="subtle">
                                                                <button type="button">{id}</button>
                                                            </Badge>
                                                        </TooltipTrigger>
                                                        {/* Shaped after `Feature Coachmark`
                                                        (`21288:37290`), minus its badge and its
                                                        dismiss action: a 320px box on 16px of
                                                        padding, an 18px bold title over a 14px
                                                        secondary body, 8px apart, with a pointer.

                                                        The surface itself needs no override — the
                                                        primitive already carries the coachmark's
                                                        `surface-default` fill, 1px border, `shadow/sm`
                                                        and radius — and `size="default"` is where its
                                                        16px padding and 18px step already live. Only
                                                        the width is set here. */}
                                                        <TooltipContent className="w-80">
                                                            <div className="flex flex-col gap-2">
                                                                {/* The variant's own name, which the wide
                                                                row shows nowhere else — repeating the
                                                                part number the reader is pointing at
                                                                would say nothing new. */}
                                                                {row.product.name && (
                                                                    <p className="font-bold">{row.product.name}</p>
                                                                )}
                                                                <p className="text-sm text-muted-foreground">
                                                                    {row.product.longDescription}
                                                                </p>
                                                            </div>
                                                            <TooltipArrow />
                                                        </TooltipContent>
                                                    </Tooltip>
                                                ) : (
                                                    <Badge variant="neutral" appearance="subtle">
                                                        {id}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            {/* `wrap` is Figma's `Type=Text` cell, which the frame uses for
                                            every plain-value column: 8px of block padding and no
                                            clipping (`15905:52980` / `15905:52982`). The badge, image
                                            and control cells stay on `Type=Default`. */}
                                            <TableCell wrap>{row.product.upc ?? ''}</TableCell>
                                            {columns.map((column) => (
                                                <TableCell key={column.attributeId} wrap align={alignFor(column)}>
                                                    {row.values[column.attributeId] ?? ''}
                                                </TableCell>
                                            ))}
                                            <TableCell align="right" className="text-muted-foreground">
                                                {PRICE_PLACEHOLDER}
                                            </TableCell>
                                            {/* NO `line-through` while this is a placeholder. The rule
                                            belongs to the wholesale FIGURE — it is a reference price
                                            the dealer does not pay — and striking the em dash instead
                                            draws a second line across the first, which reads as a
                                            rendering fault rather than as an absent value. It comes
                                            back with the number, on the number. */}
                                            <TableCell align="right" className="text-muted-foreground">
                                                {PRICE_PLACEHOLDER}
                                            </TableCell>
                                            <TableCell align="right" className="font-medium whitespace-nowrap">
                                                {formatPrice(row.product)}
                                            </TableCell>
                                            <TableCell>
                                                <VariantAvailability inventory={row.product.inventory} />
                                            </TableCell>
                                            <TableCell>
                                                <QuantityPicker
                                                    size="sm"
                                                    // Zero is the resting state of every row; see the card
                                                    // layout's picker for why the floor is named.
                                                    min={0}
                                                    // Un-orderable rows take no quantity; see the card
                                                    // layout's picker for why, and for why `=== false`.
                                                    disabled={row.product.inventory?.orderable === false}
                                                    value={quantities[id] ?? '0'}
                                                    // Named for the part number; see the card layout's picker.
                                                    aria-label={tQuantity('quantityForProduct', {
                                                        productName: id,
                                                    })}
                                                    productName={row.product.name ?? id}
                                                    onChange={(stringValue) => {
                                                        setQuantities((current) => ({ ...current, [id]: stringValue }));
                                                    }}
                                                    onBlur={() => settleQuantity(id)}
                                                />
                                            </TableCell>
                                        </TableRow>
                                        {isOpen && (
                                            // One cell spanning the table: the detail belongs to the row,
                                            // not to any column, and splitting it across the columns would
                                            // tie its layout to a count that changes per product.
                                            <TableRow id={`${id}-details`}>
                                                {/* The cell is stripped back to a container: `Variant Row
                                                Expanded` (`15905:53049`) carries its own 40px inline
                                                and 24px block padding, and the body cell's own `h-16`,
                                                `overflow-hidden` and `nowrap` would clip a panel that
                                                is 566px tall in the frame. */}
                                                <TableCell
                                                    colSpan={columns.length + fixedColumnCount}
                                                    className="h-auto overflow-visible p-0 whitespace-normal">
                                                    <div className="flex items-start gap-10 bg-secondary px-10 py-6">
                                                        <div className="flex flex-1 flex-col gap-6">
                                                            {/* The card drawer's rows one type step up —
                                                            16px here against 14px there. Identity leads,
                                                            then the catalogue's own attributes; the
                                                            reasoning for every row lives on the shared
                                                            component.

                                                            The frame puts a title above the list instead
                                                            (`15905:53049`), but the component's own
                                                            expanded state (`13579:39797`) has none, and a
                                                            heading plus a label/value list are two
                                                            reading orders where one will do. */}
                                                            <VariantDetailRows
                                                                product={row.product}
                                                                detailRows={detailRows}
                                                                className="text-base"
                                                            />
                                                        </div>
                                                        {detailImage?.link && (
                                                            // The VARIANT's own image, not the page's. The
                                                            // gallery keeps showing whatever the shopper
                                                            // selected; this drawer describes one item.
                                                            //
                                                            // 423px square on white, per the frame. Fixed
                                                            // rather than fluid because the panel's left
                                                            // column is what takes the remaining width.
                                                            <DynamicImage
                                                                src={detailImage.link}
                                                                alt={detailImage.title ?? row.product.name ?? id}
                                                                widths={[423, 846]}
                                                                className="size-[423px] shrink-0 bg-background"
                                                                imageProps={{ className: 'size-full object-contain' }}
                                                            />
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>

                    {/* Placed where the order is committed rather than beside the filters: this is a fact
                        about the ORDER, and the buyer reads it as they are about to place one.

                        `role="status"` like the truncation notice above — a condition of the page, not an
                        error the reader caused. */}
                    {activeFilters.length > 0 && hiddenItemCount > 0 && (
                        <p
                            role="status"
                            className="text-sm text-muted-foreground"
                            data-testid="product-variations-filtered-out">
                            {t('filteredOutItems', { count: hiddenItemCount })}{' '}
                            {/* The whole second sentence is the control rather than two words inside it:
                                the strings carry no markup to split on, and interpolating a fragment into
                                a translated sentence hands translators a phrase they cannot reorder.

                                A `button`, not a link: it rewrites this surface's query via
                                `setSearchParams` and has no address of its own. */}
                            <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-sm font-normal"
                                onClick={clearFilters}
                                data-testid="product-variations-filtered-out-clear">
                                {t('clearFiltersToInclude', { count: hiddenItemCount })}
                            </Button>
                        </p>
                    )}

                    {/* ORDER — the band that totals the pending order and commits it. Presentational:
                        the figures are derived here and the fetcher stays here, because publishing the
                        basket revision and reporting the outcome are not presentation. */}
                    <ProductOrderBar
                        lines={orderLines}
                        total={orderTotal}
                        itemCount={orderItemCount}
                        submitting={orderFetcher.state !== 'idle'}
                        Form={orderFetcher.Form}
                        action={resourceRoutes.cartSetAdd}
                    />
                </>
            )}

            {/* Mounted only while a row is pending, so the dialog's own state cannot outlive the row it
                asks about. Dismissing by Escape or the overlay runs the SAME path as "No" — a
                confirmation that treats "not answered" as "yes" is not a confirmation. */}
            {pendingQuantity && (
                <ConfirmationDialog
                    open
                    onOpenChange={(next) => {
                        if (!next) rejectPendingQuantity();
                    }}
                    title={t('largeQuantityTitle')}
                    description={t('largeQuantityDescription', {
                        // `quantity`, deliberately not `count`: i18next reads `count` as a plural
                        // selector and would look for `_one`/`_other` variants of this key.
                        //
                        // Grouped for the locale, because this is the ONE place in the table where a
                        // four-digit figure actually reaches the page — the availability counts are capped
                        // at a threshold well below it. `1500` in a sentence asking "is that correct?"
                        // reads as a part number; `1,500` reads as the quantity it is.
                        quantity: pendingQuantity.quantity.toLocaleString(i18nLanguage),
                        partNumber: pendingQuantity.id,
                    })}
                    cancelButtonText={t('largeQuantityCancel')}
                    confirmButtonText={t('largeQuantityConfirm')}
                    onCancel={rejectPendingQuantity}
                    onConfirm={confirmPendingQuantity}
                />
            )}
        </section>
    );
}

/**
 * Loading state for the table's Suspense boundary.
 *
 * Occupies height rather than rendering nothing: the section sits below the two-column layout, and
 * injecting a many-row table into a collapsed box moves everything under it.
 */
export function ProductVariationsTableFallback({ className }: { className?: string }): ReactElement {
    return (
        <section className={cn('flex flex-col gap-4', className)} aria-hidden="true">
            <Skeleton className="h-8 w-32" />
            <div className="flex flex-col gap-2">
                {Array.from({ length: FALLBACK_ROW_COUNT }, (_, index) => (
                    <Skeleton key={index} className="h-10 w-full" />
                ))}
            </div>
        </section>
    );
}
